import SearchBar from '_/space-management/components/SearchBar';
import Text from '_/space-management/components/Text';
import Title from '_/space-management/components/Title';
import { colors, spacing } from '_/space-management/constants/theme';
import { usersSlotsApi } from '_/space-management/services/api';
import { UsersModel } from '_/space-management/services/models/users.model';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet, FlatList, ActivityIndicator, Platform } from 'react-native';
import Modal from 'react-native-modal';
import Member from './Member';
import { showMessage } from 'react-native-flash-message';

interface SearchForMembersModalProps {
  visible: boolean;
  hideDropdown: () => void;
  slotId: string;
  setForMember: React.Dispatch<React.SetStateAction<UsersModel | undefined>>;
}

export default function SearchForMembersModal({
  visible,
  hideDropdown,
  slotId,
  setForMember,
}: SearchForMembersModalProps) {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<UsersModel[]>([]);
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const { t } = useTranslation();
  const delayDebounceRef = useRef<NodeJS.Timeout>(null);

  const getUsersSlot = useCallback(async (slotId: string, searchParams?: { search?: string }) => {
    setLoading(true);
    try {
      const searchTerm = searchParams?.search;
      const result = await usersSlotsApi.getItem(slotId, {
        params: {
          $limit: 30,
          ...(searchTerm && {
            $or: [
              { name: { $iLike: `%${searchTerm}%` } },
              { email: { $iLike: `%${searchTerm}%` } },
            ],
          }),
          '$sort[name]': 1,
        },
      });

      setMembers(result.data);
    } catch (error) {
      showMessage({
        message: t('spaceManage.error'),
        // description: t('spaceManage.workstationScreen.searchMemberModal.searchError'),
        description: t('inviteeSaga.getInviteeDetailsError'),
        backgroundColor: colors.errorRed,
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!visible) return;

    const delaySearch = () => {
      if (delayDebounceRef.current) clearTimeout(delayDebounceRef.current);

      return setTimeout(
        () => {
          getUsersSlot(slotId, { search: searchValue });
        },
        searchValue ? 1500 : 0
      );
    };

    delayDebounceRef.current = delaySearch();

    return () => {
      if (delayDebounceRef.current) clearTimeout(delayDebounceRef.current);
    };
  }, [getUsersSlot, searchValue, slotId, visible]);

  return (
    <Modal
      style={styles.modal}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      isVisible={visible}
      coverScreen
      hasBackdrop
      propagateSwipe
      backdropOpacity={0.5}
      onBackdropPress={() => hideDropdown()}
      onBackButtonPress={() => hideDropdown()}
    >
      <Title
        title={t('spaceManage.workstationScreen.searchMemberModal.searchMember')}
        hasCloseButton
        onPress={() => hideDropdown()}
        style={[styles.title, { maxWidth: '90%' }]}
      />
      <View style={styles.modalContent}>
        <SearchBar
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          placeholder={t('spaceManage.workstationScreen.searchMemberModal.search')}
        />
        <FlatList
          ListEmptyComponent={
            loading ? (
              <ActivityIndicator color={colors.black} />
            ) : (
              <Text>{t('spaceManage.workstationScreen.searchMemberModal.noResults')} </Text>
            )
          }
          showsVerticalScrollIndicator={false}
          keyExtractor={(item) => item.id}
          style={{ marginTop: 32 }}
          data={members}
          keyboardShouldPersistTaps="always"
          renderItem={({ item }) => (
            <Member user={item} setForMember={setForMember} hideDropdown={hideDropdown} />
          )}
        />
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modal: {
    flex: 1,
    width: '100%',
    ...(Platform.OS === 'web' && { maxWidth: 768 }),
    margin: 0,
    marginTop: 80,
    alignSelf: 'center',
    justifyContent: 'flex-start',
    backgroundColor: colors.clear,
    borderTopRightRadius: 20,
    borderTopLeftRadius: 20,
    paddingVertical: 20,
  },
  modalContent: {
    paddingHorizontal: spacing.md,
  },
  title: {
    marginBottom: spacing.md,
  },
});
